import type { CodeableConcept, PatientContact } from '@medplum/fhirtypes';
import { V3_ROLE_CODE_SYSTEM } from './familyRelationship';

/**
 * HL7 v2 table 0131 — Contact Role. Used on `Patient.contact.relationship` to mark an entry as the
 * patient's emergency contact (code `C`). See https://terminology.hl7.org/CodeSystem-v2-0131.html.
 */
export const V2_CONTACT_RELATIONSHIP_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v2-0131';

/** `C` — Emergency Contact in {@link V2_CONTACT_RELATIONSHIP_SYSTEM}. */
export const EMERGENCY_CONTACT_RELATIONSHIP_CODE = 'C';

const EMERGENCY_CONTACT_DISPLAY = 'Emergency Contact';

/**
 * Curated personal-relationship options for an emergency contact, coded with
 * {@link V3_ROLE_CODE_SYSTEM}. Distinct from the v2-0131 contact *role* (`C`), which is always
 * stamped separately. Displays are English FHIR `coding.display` values; UI hosts localize via i18n.
 */
export const EMERGENCY_CONTACT_PERSONAL_RELATIONSHIPS = [
  { code: 'SPS', display: 'Spouse' },
  { code: 'DOMPART', display: 'Domestic partner' },
  { code: 'PRN', display: 'Parent' },
  { code: 'CHILD', display: 'Child' },
  { code: 'SIB', display: 'Sibling' },
  { code: 'GRPRN', display: 'Grandparent' },
  { code: 'FAMMEMB', display: 'Family member' },
  { code: 'FRND', display: 'Friend' },
  { code: 'NBOR', display: 'Neighbor' },
  { code: 'ROOM', display: 'Roommate' },
  { code: 'GUARD', display: 'Guardian' },
  { code: 'OTH', display: 'Other' },
] as const;

export type EmergencyContactPersonalRelationshipCode =
  (typeof EMERGENCY_CONTACT_PERSONAL_RELATIONSHIPS)[number]['code'];

const PERSONAL_RELATIONSHIP_BY_CODE: ReadonlyMap<string, string> = new Map(
  EMERGENCY_CONTACT_PERSONAL_RELATIONSHIPS.map((entry) => [entry.code, entry.display]),
);

/** Demographics for the patient's emergency contact, mapped to/from FHIR `Patient.contact`. */
export type EmergencyContactFields = {
  name: string;
  phone: string | null;
  /** v3-RoleCode when known; null for legacy free-text-only entries. */
  relationshipCode: string | null;
  /** Display/text for the personal relationship (for UI). */
  relationship: string | null;
};

/**
 * Write shape for an emergency-contact entry on FHIR `Patient.contact` (v2-0131 `C`).
 * Mirrors {@link EmergencyContactFields} without the derived `relationship` display — callers pass
 * demographics; the service stamps coding + display when building the FHIR entry.
 */
export type EmergencyContactInput = {
  name: string;
  phone?: string | null;
  relationshipCode?: string | null;
};

const isEmergencyContactCoding = (concept: CodeableConcept | undefined): boolean => {
  if (!concept) return false;
  if (
    concept.coding?.some((coding) => {
      const code = coding.code?.trim();
      if (code !== EMERGENCY_CONTACT_RELATIONSHIP_CODE && code !== 'EP') return false;
      // Accept exact system, versioned system URLs, or a bare C/EP code.
      return !coding.system || coding.system === V2_CONTACT_RELATIONSHIP_SYSTEM || coding.system.includes('v2-0131');
    })
  ) {
    return true;
  }
  const text = concept.text?.trim().toLowerCase();
  return text === 'emergency contact' || text === 'emergency contact person';
};

/** True when a `Patient.contact` entry is tagged as an emergency contact (v2-0131 `C`). */
export const isEmergencyContact = (contact: PatientContact): boolean =>
  contact.relationship?.some(isEmergencyContactCoding) ?? false;

const contactNameText = (contact: PatientContact): string | null => {
  const text = contact.name?.text?.trim();
  if (text) return text;
  const given = contact.name?.given?.filter(Boolean).join(' ').trim();
  const family = contact.name?.family?.trim();
  const composed = [given, family].filter(Boolean).join(' ').trim();
  return composed || null;
};

const personalRelationshipConcept = (contact: PatientContact): CodeableConcept | undefined =>
  contact.relationship?.find((concept) => !isEmergencyContactCoding(concept));

/** Resolve a personal-relationship CodeableConcept to code + display. */
const personalRelationshipFields = (
  concept: CodeableConcept | undefined,
): Pick<EmergencyContactFields, 'relationshipCode' | 'relationship'> => {
  if (!concept) return { relationshipCode: null, relationship: null };

  const roleCoding =
    concept.coding?.find((coding) => coding.system === V3_ROLE_CODE_SYSTEM && coding.code) ??
    concept.coding?.find((coding) => coding.code);
  const code = roleCoding?.code?.trim() || null;
  const display =
    concept.text?.trim() ||
    roleCoding?.display?.trim() ||
    (code ? (PERSONAL_RELATIONSHIP_BY_CODE.get(code) ?? null) : null);

  return { relationshipCode: code, relationship: display };
};

/** Look up the English FHIR display for a curated personal-relationship code. */
export const emergencyContactRelationshipDisplay = (code: string | null | undefined): string | null => {
  if (!code) return null;
  return PERSONAL_RELATIONSHIP_BY_CODE.get(code) ?? null;
};

/** First emergency-contact entry on a FHIR Patient, or `null` when none is present. */
export const extractEmergencyContact = (contacts: PatientContact[] | undefined): EmergencyContactFields | null => {
  const entry = contacts?.find(isEmergencyContact);
  if (!entry) return null;
  const name = contactNameText(entry);
  if (!name) return null;
  const phone =
    entry.telecom?.find((telecom) => telecom.system === 'phone' && telecom.value?.trim())?.value?.trim() ?? null;
  const { relationshipCode, relationship } = personalRelationshipFields(personalRelationshipConcept(entry));
  return {
    name,
    phone,
    relationshipCode,
    relationship,
  };
};

/**
 * Build a FHIR `Patient.contact` entry for an emergency contact. Always stamps v2-0131 `C`; optional
 * personal relationship is a second CodeableConcept with v3-RoleCode when a curated code is supplied.
 */
export const buildEmergencyContactEntry = (fields: EmergencyContactFields): PatientContact => {
  const relationship: CodeableConcept[] = [
    {
      coding: [
        {
          system: V2_CONTACT_RELATIONSHIP_SYSTEM,
          code: EMERGENCY_CONTACT_RELATIONSHIP_CODE,
          display: EMERGENCY_CONTACT_DISPLAY,
        },
      ],
      text: EMERGENCY_CONTACT_DISPLAY,
    },
  ];

  const code = fields.relationshipCode?.trim() || null;
  const display = fields.relationship?.trim() || emergencyContactRelationshipDisplay(code) || null;
  if (code || display) {
    relationship.push({
      ...(code
        ? {
            coding: [
              {
                system: V3_ROLE_CODE_SYSTEM,
                code,
                ...(display ? { display } : {}),
              },
            ],
          }
        : {}),
      ...(display ? { text: display } : {}),
    });
  }

  const phone = fields.phone?.trim();
  return {
    relationship,
    name: { text: fields.name.trim() },
    ...(phone ? { telecom: [{ system: 'phone' as const, value: phone }] } : {}),
  };
};

/**
 * Merge an emergency-contact edit into an existing `Patient.contact` array. Preserves non-emergency
 * contacts. `null` clears every emergency-contact entry; `undefined` leaves contacts unchanged.
 */
export const applyEmergencyContactToContacts = (
  existing: PatientContact[] | undefined,
  fields: EmergencyContactFields | null | undefined,
): PatientContact[] | undefined => {
  if (fields === undefined) return existing;

  const others = (existing ?? []).filter((contact) => !isEmergencyContact(contact));
  if (fields === null) {
    return others.length > 0 ? others : undefined;
  }

  return [...others, buildEmergencyContactEntry(fields)];
};
